import React, {useState} from "react"
import {ApplicationResubmitForms} from "../ReevaluateApplication/ReevaluatePage"
import {useModal} from "react-modal-hook"
import {useNavigate} from "react-router-dom"
import {OkDocument} from "../../resources/common"
import {BusinessProductApplication} from "../../resources/productApplication"
import {Officer} from "../../resources/application"
import ReevaluateOfficerForm, {
	ReevaluatedOfficerApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateOfficerForm/ReevaluateOfficerForm"
import ReevaluateProductApplicationOfficerConfirmationModal from "../../components/ReevaluateProductApplication/ReevaluateProductApplicationConfirmationModal/ReevaluateProductApplicationOfficerConfirmationModal"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import ProductApplicationDetails from "./ProductApplicationDetails"
import {identificationFieldBeforeUpdateRequest} from "../ReevaluateApplication/ApplicationDetails"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateProductApplicationOfficerPageProps {
	application: OkDocument<BusinessProductApplication>
	officer: Officer
	officerId: string
}

function toOfficerFormData(officer?: Officer): ReevaluatedOfficerApplicationData | undefined {
	if (!officer || !officer.address || !officer.dateOfBirth || !officer.email || !officer.phone) {
		return undefined
	}

	return {
		fullName: officer.fullName,
		address: officer.address,
		ssn: officer.ssn ?? undefined,
		nationality: officer.nationality ?? undefined,
		passport: officer.passport ?? undefined,
		matriculaConsular: officer.matriculaConsular ?? undefined,
		dateOfBirth: officer.dateOfBirth,
		email: officer.email,
		phone: officer.phone,
		occupation: officer.occupation ?? undefined,
		sourceOfIncome: officer.sourceOfIncome ?? undefined,
		annualIncome: officer.annualIncome ?? undefined,
	}
}

export default function ReevaluateProductApplicationOfficerContainer({
	application,
	officer,
	officerId,
}: ReevaluateProductApplicationOfficerPageProps) {
	const navigate = useNavigate()
	const officerFormData = toOfficerFormData(officer)

	const [officerReevaluatedData, setOfficerReevaluatedData] = useState<ReevaluatedOfficerApplicationData | undefined>(
		officerFormData
	)
	const [conversionError, setConversionError] = useState(false)

	const [showOfficerConfirmationModal, closeOfficerConfirmationModal] = useModal(() => {
		if (!officerReevaluatedData) {
			setConversionError(true)
			return <></>
		}
		return (
			<ReevaluateProductApplicationOfficerConfirmationModal
				onSuccess={() => navigate(`/product-applications/${application.data.id}`)}
				close={closeOfficerConfirmationModal}
				productApplicationId={application.data.id}
				officerId={officerId}
				reevaluatedData={officerReevaluatedData}
			/>
		)
	}, [officerReevaluatedData])

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]

	const form = (
		<ProductApplicationDetails
			title={title}
			meta={meta}
			productApplicationId={application.data.id}
			status={application.data.attributes.status}
			formId={ApplicationResubmitForms.officerForm}
		>
			{officerFormData && (
				<ReevaluateOfficerForm
					officer={officerFormData}
					submitCallback={(values) => {
						const currentValues = {
							ssn: officer.ssn,
							passport: officer.passport,
							matriculaConsular: officer.matriculaConsular,
						}
						const identificationFields = identificationFieldBeforeUpdateRequest({
							currentValues: currentValues,
							updatedValues: values,
						})

						setOfficerReevaluatedData({
							...values,
							...identificationFields,
						})
						showOfficerConfirmationModal()
					}}
					formId={ApplicationResubmitForms.officerForm}
				/>
			)}
		</ProductApplicationDetails>
	)

	if (conversionError) {
		return (
			<Admonition
				type="is-danger"
				title="Failed to process resubmission data, please reach administrator."
				message={""}
			/>
		)
	}

	if (!officerFormData) {
		return (
			<Admonition
				type="is-danger"
				title="Could not be able to show resubmit form, please reach administator."
				message={""}
			/>
		)
	}

	return form
}
